import React, { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { TabView, TabPanel } from 'primereact/tabview';
import { useClienteAuth } from '../context/ClienteAuthContext';

function AuthDialog({ visible, onHide }) {
    const { clienteLogin, clienteRegister } = useClienteAuth();
    const toast = useRef(null);
    const [activeTab, setActiveTab] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Login form
    const [loginData, setLoginData] = useState({ username: '', password: '' });

    // Register form
    const [regData, setRegData] = useState({
        username: '',
        password: '',
        confirmPassword: '',
        nombres: '',
        apellidoPaterno: '',
        apellidoMaterno: '',
        email: ''
    });

    const resetForms = () => {
        setLoginData({ username: '', password: '' });
        setRegData({
            username: '', password: '', confirmPassword: '',
            nombres: '', apellidoPaterno: '', apellidoMaterno: '',
            email: ''
        });
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!loginData.username || !loginData.password) {
            toast.current.show({ severity: 'warn', summary: 'Campos incompletos', detail: 'Ingrese usuario y contraseña', life: 3000 });
            return;
        }

        setIsLoading(true);
        try {
            await clienteLogin(loginData.username, loginData.password);
            toast.current.show({ severity: 'success', summary: 'Bienvenido', detail: 'Sesión iniciada correctamente', life: 2000 });
            setTimeout(() => { resetForms(); onHide(); }, 1500);
        } catch (error) {
            const msg = error.response?.data?.message || 'Usuario o contraseña incorrectos';
            toast.current.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        const { username, password, confirmPassword, nombres, apellidoPaterno, email } = regData;

        if (!username || !password || !nombres || !apellidoPaterno || !email) {
            toast.current.show({ severity: 'warn', summary: 'Campos incompletos', detail: 'Complete todos los campos obligatorios', life: 3000 });
            return;
        }

        if (password !== confirmPassword) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Las contraseñas no coinciden', life: 3000 });
            return;
        }

        if (password.length < 6) {
            toast.current.show({ severity: 'warn', summary: 'Contraseña débil', detail: 'Mínimo 6 caracteres', life: 3000 });
            return;
        }

        setIsLoading(true);
        try {
            await clienteRegister({
                username: regData.username,
                password: regData.password,
                nombres: regData.nombres,
                apellidoPaterno: regData.apellidoPaterno,
                apellidoMaterno: regData.apellidoMaterno,
                email: regData.email
            });
            toast.current.show({ severity: 'success', summary: '¡Cuenta creada!', detail: 'Registro exitoso', life: 2000 });
            setTimeout(() => { resetForms(); onHide(); }, 1500);
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al registrarse';
            toast.current.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                header="Mi Cuenta"
                visible={visible}
                style={{ width: '440px' }}
                onHide={() => { resetForms(); onHide(); }}
                modal
                className="p-fluid"
            >
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    {/* TAB LOGIN */}
                    <TabPanel header="Iniciar Sesión" leftIcon="pi pi-sign-in mr-2">
                        <form onSubmit={handleLogin}>
                            <div className="p-field mb-3">
                                <label htmlFor="login-user" className="font-medium">Usuario</label>
                                <InputText
                                    id="login-user"
                                    value={loginData.username}
                                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                                    placeholder="Tu nombre de usuario"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="p-field mb-4">
                                <label htmlFor="login-pass" className="font-medium">Contraseña</label>
                                <Password
                                    id="login-pass"
                                    value={loginData.password}
                                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                                    toggleMask
                                    feedback={false}
                                    placeholder="Tu contraseña"
                                    disabled={isLoading}
                                />
                            </div>
                            <Button
                                label={isLoading ? "Ingresando..." : "Iniciar Sesión"}
                                icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-sign-in"}
                                type="submit"
                                disabled={isLoading}
                                className="w-full"
                            />
                        </form>
                    </TabPanel>

                    {/* TAB REGISTRO */}
                    <TabPanel header="Crear Cuenta" leftIcon="pi pi-user-plus mr-2">
                        <form onSubmit={handleRegister}>
                            <div className="grid">
                                <div className="col-12 mb-2">
                                    <label className="font-medium">Nombres *</label>
                                    <InputText value={regData.nombres} onChange={(e) => setRegData({ ...regData, nombres: e.target.value })} placeholder="Tus nombres" disabled={isLoading} />
                                </div>
                                <div className="col-6 mb-2">
                                    <label className="font-medium">Apellido Paterno *</label>
                                    <InputText value={regData.apellidoPaterno} onChange={(e) => setRegData({ ...regData, apellidoPaterno: e.target.value })} placeholder="Ap. Paterno" disabled={isLoading} />
                                </div>
                                <div className="col-6 mb-2">
                                    <label className="font-medium">Apellido Materno</label>
                                    <InputText value={regData.apellidoMaterno} onChange={(e) => setRegData({ ...regData, apellidoMaterno: e.target.value })} placeholder="Ap. Materno" disabled={isLoading} />
                                </div>

                                <div className="col-12 mb-2">
                                    <label className="font-medium">Email *</label>
                                    <InputText type="email" value={regData.email} onChange={(e) => setRegData({ ...regData, email: e.target.value })} placeholder="tucorreo@ejemplo.com" disabled={isLoading} />
                                </div>

                                <div className="col-12 mb-2">
                                    <label className="font-medium">Nombre de Usuario *</label>
                                    <InputText value={regData.username} onChange={(e) => setRegData({ ...regData, username: e.target.value })} placeholder="Elige un usuario" disabled={isLoading} />
                                </div>
                                <div className="col-6 mb-2">
                                    <label className="font-medium">Contraseña *</label>
                                    <Password value={regData.password} onChange={(e) => setRegData({ ...regData, password: e.target.value })} toggleMask feedback={false} placeholder="Mínimo 6 chars" disabled={isLoading} />
                                </div>
                                <div className="col-6 mb-2">
                                    <label className="font-medium">Confirmar *</label>
                                    <Password value={regData.confirmPassword} onChange={(e) => setRegData({ ...regData, confirmPassword: e.target.value })} toggleMask feedback={false} placeholder="Repetir" disabled={isLoading} />
                                </div>
                            </div>

                            <Button
                                label={isLoading ? "Registrando..." : "Crear Cuenta"}
                                icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-user-plus"}
                                type="submit"
                                disabled={isLoading}
                                className="w-full mt-2"
                            />
                        </form>
                    </TabPanel>
                </TabView>
            </Dialog>
        </>
    );
}

export default AuthDialog;
